/**
 * Mosterd Drawer Menu — Web Component
 * Custom mobile drawer replacing Dwell's built-in drawer.
 */

class MosterdDrawerMenu extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._focusableElements = null;
    this._previousActiveElement = null;
    this._ready = false;
  }

  connectedCallback() {
    // Bind methods once
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onHamburgerClick = this._onHamburgerClick.bind(this);

    // Use event delegation on the component for ALL click handling.
    // This avoids the connectedCallback timing issue where children
    // aren't parsed yet and querySelector returns null.
    this.addEventListener('click', function(e) {
      // Hamburger toggle
      if (e.target.closest('[data-mosterd-drawer-toggle]')) {
        e.preventDefault();
        e.stopPropagation();
        this._ensureReady();
        this.open();
        return;
      }
      // Close button
      if (e.target.closest('[data-drawer-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      // Overlay
      if (e.target.closest('[data-drawer-overlay]') && !e.target.closest('[data-drawer-panel]')) {
        this.close();
        return;
      }
    }.bind(this));

    // Listen for custom open event
    document.addEventListener('mosterd:drawer:open', function() {
      this._ensureReady();
      this.open();
    }.bind(this));

    // Don't intercept Dwell's hamburger — use Dwell's native drawer menu
  }

  _ensureReady() {
    if (this._ready) return;
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.panel = this.querySelector('[data-drawer-panel]');
    this.closeBtn = this.querySelector('[data-drawer-close]');
    this._ready = true;
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeyDown);
    var hamburger = document.querySelector('.header__icon--menu');
    if (hamburger) {
      hamburger.removeEventListener('click', this._onHamburgerClick);
    }
  }

  _interceptHamburger() {
    var self = this;
    var attach = function() {
      var hamburger = document.querySelector('.header__icon--menu');
      if (hamburger) {
        hamburger.addEventListener('click', self._onHamburgerClick, { capture: true });
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach);
    } else {
      attach();
    }
  }

  _onHamburgerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    var details = document.querySelector('#Details-menu-drawer-container');
    if (details && details.open) {
      details.open = false;
    }
    // Remove Dwell's scroll-lock that dialog.js sets when <details> opens
    document.documentElement.removeAttribute('scroll-lock');

    this._ensureReady();
    this.open();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;

    this._previousActiveElement = document.activeElement;

    if (this.overlay) this.overlay.classList.add('is-open');
    if (this.panel) this.panel.classList.add('is-open');

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    document.addEventListener('keydown', this._onKeyDown);

    var closeBtn = this.closeBtn;
    requestAnimationFrame(function() {
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;

    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this.panel) this.panel.classList.remove('is-open');

    document.body.style.overflow = '';
    document.body.style.touchAction = '';

    document.removeEventListener('keydown', this._onKeyDown);

    if (this._previousActiveElement) {
      this._previousActiveElement.focus();
      this._previousActiveElement = null;
    }

    this._focusableElements = null;
  }

  _trapFocus(e) {
    if (!this.panel) return;

    if (!this._focusableElements) {
      this._focusableElements = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
    }

    var focusable = this._focusableElements;
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  get isOpen() {
    return this._isOpen;
  }
}

if (!customElements.get('mosterd-drawer-menu')) {
  customElements.define('mosterd-drawer-menu', MosterdDrawerMenu);
}
